import React, { useEffect, useRef } from 'react';
import { m, AnimatePresence, useDragControls, type MotionProps } from 'framer-motion';
import { DRAWER_GESTURE_CONFIG } from '@/constants/animation';
import { useScrollLock } from '@/hooks/ui/useScrollLock';

interface NativeDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  backdropClassName?: string;
  panelClassName?: string;
  zIndex?: number;
  transition?: MotionProps['transition'];
}

/**
 * NativeDrawer - 移动端防御基线封装
 * - 锁定 html+body 滚动，恢复同帧完成
 * - 100dvh 高度，touch-action/overscroll 基线
 * - 阻断穿透，提供 aria 属性与焦点回退
 */
export const NativeDrawer: React.FC<NativeDrawerProps> = ({
  isOpen,
  onClose,
  children,
  backdropClassName = '',
  panelClassName = '',
  zIndex = 10000, // 顶层浮层基准
  transition
}) => {
  const backdropRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const lastActiveElement = useRef<HTMLElement | null>(null);
  const dragControls = useDragControls();
  const intentRef = useRef({
    startX: 0,
    startY: 0,
    active: false,
    decided: false
  });

  const INTENT_SLOP_PX = 12;
  const CLOSE_OFFSET_PX = 160;
  const CLOSE_VELOCITY = 600;

  // 锁定滚动（html+body）
  useScrollLock(isOpen);

  // 焦点管理：打开时聚焦 panel，关闭时回到触发源
  useEffect(() => {
    if (isOpen) {
      lastActiveElement.current = document.activeElement as HTMLElement | null;
      panelRef.current?.focus();
    } else {
      lastActiveElement.current?.focus?.();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (import.meta.env.MODE === 'production') return;
    if (typeof window === 'undefined') return;
    if (!(window as any).__DEBUG_CART_DRAWER_WIDTH__) return;

    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    const delta = Math.abs(rect.width - window.innerWidth);
    if (delta > 1) {
      console.warn('[NativeDrawer] width mismatch', {
        drawerWidth: rect.width,
        viewportWidth: window.innerWidth,
        delta
      });
    }
  }, [isOpen]);

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const drawerVariants = {
    hidden: { x: '100%' },
    visible: { x: '0%' },
    exit: { x: '100%' }
  };

  return (
    <AnimatePresence onExitComplete={() => { /* scrollLock 由 hook 处理 */ }}>
      {isOpen && (
        <>
          <m.div
            ref={backdropRef}
            key="backdrop"
            className={`fixed inset-0 bg-black/40 backdrop-blur-sm touch-none ${backdropClassName}`}
            style={{ zIndex }}
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.3 }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onClick={onClose}
          />

          <m.div
            ref={panelRef}
            key="drawer"
            role="dialog"
            aria-modal="true"
            tabIndex={-1}
            // legacy: fixed inset-y-0 right-0 w-screen max-w-full sm:max-w-[440px]
            className={`fixed inset-y-0 inset-x-0 sm:left-auto sm:right-0 w-full sm:w-[440px] sm:max-w-[440px] bg-background shadow-2xl flex flex-col touch-pan-y overscroll-contain focus:outline-none min-w-0 overflow-x-hidden ${panelClassName}`}
            style={{
              zIndex: zIndex + 1,
              touchAction: 'pan-y',
              overscrollBehaviorX: 'none',
              overscrollBehaviorY: 'contain',
              minHeight: '100dvh'
            }}
            variants={drawerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={transition}
            drag="x"
            dragControls={dragControls}
            dragListener={false}
            {...DRAWER_GESTURE_CONFIG}
            dragMomentum={false}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            onPointerDown={(e) => {
              intentRef.current = {
                startX: e.clientX,
                startY: e.clientY,
                active: true,
                decided: false
              };
            }}
            onPointerMove={(e) => {
              const state = intentRef.current;
              if (!state.active || state.decided) return;

              const dx = e.clientX - state.startX;
              const dy = e.clientY - state.startY;
              if (Math.abs(dx) < INTENT_SLOP_PX && Math.abs(dy) < INTENT_SLOP_PX) return;

              state.decided = true;
              if (Math.abs(dx) > Math.abs(dy)) {
                dragControls.start(e);
              }
            }}
            onPointerUp={() => {
              intentRef.current.active = false;
              intentRef.current.decided = false;
            }}
            onPointerCancel={() => {
              intentRef.current.active = false;
              intentRef.current.decided = false;
            }}
            onDragEnd={(_, info) => {
              if (info.offset.x > CLOSE_OFFSET_PX || info.velocity.x > CLOSE_VELOCITY) {
                onClose();
              }
            }}
          >
            {children}
          </m.div>
        </>
      )}
    </AnimatePresence>
  );
};
